import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CommentToggleButton, ExpandableCommentTableRows } from './CommentExpand';
import { ResultSectionWithTitle } from './ResultSectionEmpty';
import { Activity } from 'lucide-react';

interface Covid19ResultProps {
	resultMetadata: unknown;
}

/** COVID-19 (중소병원 대응 실습) 결과 */
export default function Covid19Result({ resultMetadata }: Covid19ResultProps): React.ReactElement {
	const { t } = useTranslation(['class', 'scenario']);
	const title = t('simulationResults.tablePerformance');
	const m = resultMetadata as Record<string, unknown> | null | undefined;

	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

	const translationPrefix = 'study_result.covid';

	// PPE(개인보호구) 영어명 → 한국어 매핑
	const translatePPE = (englishName: string): string => {
		// _PPE, _ppe 같은 접미사 제거
		const cleanName = englishName.replace(/_(PPE|ppe)$/i, '');
		
		const ppeMap: Record<string, string> = {
			'HandSanitizer': '손소독제',
			'Gown': '가운',
			'N95Mask': 'N95 마스크',
			'FaceShield': '페이스 실드',
			'OuterGloves': '외과용 장갑',
			'InnerGloves': '내과용 장갑',
			'Shoes': '신발',
			'LevelDProtectionGown': '레벨D 보호복',
			'Goggles': '고글',
			'Goggle': '고글',
			'Mask': '마스크',
			'Gloves': '장갑',
			'HairCap': '헤어캡',
			'SurgicalMask': '수술용 마스크',
		};
		
		// 정확히 일치하는 매핑이 있으면 반환
		if (ppeMap[cleanName]) {
			return ppeMap[cleanName];
		}
		
		// 대소문자 무시하고 매핑 찾기
		const lowerCleanName = cleanName.toLowerCase();
		for (const [key, value] of Object.entries(ppeMap)) {
			if (key.toLowerCase() === lowerCleanName) {
				return value;
			}
		}
		
		// 매핑이 없으면 빈 문자열 반환 → itemKey로 i18n 번역하도록 폴백
		return '';
	};

	if (m == null || typeof m !== 'object') {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">데이터를 불러오는 중입니다...</div>
			</ResultSectionWithTitle>
		);
	}

	const evaluation = m.covid19Evaluation as Record<string, unknown> | undefined;
	if (!evaluation || typeof evaluation !== 'object') {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">평가 데이터가 없습니다.</div>
			</ResultSectionWithTitle>
		);
	}

	const groups = evaluation.group as Array<Record<string, unknown>> | undefined;
	if (!Array.isArray(groups) || groups.length === 0) {
		return (
			<ResultSectionWithTitle title={title} lead="activity">
				<div className="text-gray-500 text-sm">평가 데이터가 없습니다.</div>
			</ResultSectionWithTitle>
		);
	}

	return (
		<div className="space-y-6 !mt-6 !mb-4">
			<h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5 min-w-0">
				<div className="w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 bg-sky-200/55 border-sky-200/80">
					<Activity size={20} className="text-sky-800" />
				</div>
				<span className="truncate min-w-0">{title}</span>
			</h3>

			<div className="rounded-xl border border-gray-200 overflow-hidden bg-white overflow-x-auto">
				<table className="w-full text-sm text-center">
					<thead>
						<tr className="bg-gray-50 border-b border-gray-200">
							<th className="px-4 py-3 font-semibold text-gray-700 text-center w-12">No</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-start">평가항목</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-start">내용</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-center">accuracy</th>
							<th className="px-4 py-3 font-semibold text-gray-700 text-center w-24 whitespace-nowrap">코멘트</th>
						</tr>
					</thead>
					<tbody>
						{groups.map((group, idx) => {
							const isExpanded = expandedIndex === idx;
							const toggleComment = () => setExpandedIndex((prev) => (prev === idx ? null : idx));

							const correctCount = group.correct_count as number || 0;
							const totalCount = group.total_count as number || 0;
							const accuracy = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
							const groupKey = group.key as string || '';
							const groupName = group.name as string || '';

							const cards = group.cards as Array<Record<string, unknown>> | undefined;
							const firstCard = Array.isArray(cards) && cards.length > 0 ? cards[0] : null;
							const items = firstCard ? (firstCard.items as Array<Record<string, unknown>> | undefined) : undefined;
							const debriefingKey = firstCard ? (firstCard.debriefing_key as string) : '';

							// 코멘트 내용: debriefing 필드 우선, 없으면 번역 키
							const debriefing = firstCard ? (firstCard.debriefing as string) : '';
							const comment = debriefing || (debriefingKey ? `${translationPrefix}.${debriefingKey}.Debriefing` : '');

							return (
								<ExpandableCommentTableRows
									key={idx}
									isExpanded={isExpanded}
									toggleComment={toggleComment}
									colSpan={5}
									comment={comment}
									useTranslation={!debriefing}
								>
									<td className="px-4 py-3 text-center text-gray-900">{idx + 1}</td>
									<td className="px-4 py-3 text-start text-gray-900">
										{groupName || <Trans i18nKey={`${translationPrefix}.${groupKey}`} ns="scenario" />}
									</td>
									<td className="px-4 py-3 text-start text-gray-700 text-sm">
										<div className="flex flex-wrap gap-x-1 gap-y-0.5">
											{Array.isArray(items) && items.map((item, itemIdx) => {
												const pass = item.pass as boolean;
												const itemKey = item.key as string || '';
												
												// PPE 키값을 한국어로 번역 시도, 실패하면 i18n 사용
												const translatedText = translatePPE(itemKey);
												
												// detail 필드에서 동적 파라미터 추출
												// 백엔드가 "8,8" 형태의 문자열로 주면 {0: 8, 1: 8} 형태로 변환
												const detail = item.detail;
												let i18nParams: Record<string, unknown> = {};
												
												if (typeof detail === 'string' && detail.trim() !== '') {
													// "8,8" -> ["8", "8"] -> {0: "8", 1: "8"}
													const values = detail.split(',').map(v => v.trim());
													i18nParams = Object.fromEntries(values.map((v, idx) => [String(idx), v]));
												} else if (detail && typeof detail === 'object') {
													// 이미 객체 형태면 그대로 사용
													i18nParams = detail as Record<string, unknown>;
												}
												
												return (
													<React.Fragment key={itemIdx}>
														{itemIdx > 0 && <span className="text-gray-400">,</span>}
														<span className={pass ? '' : 'text-red-600 font-medium'}>
															{translatedText || t(`scenario:${translationPrefix}.${itemKey}`, i18nParams)}
														</span>
													</React.Fragment>
												);
											})}
										</div>
									</td>
									<td className="px-4 py-3 text-center text-gray-900 font-medium">
										{accuracy}%
									</td>
									<td className="px-4 py-3 text-center text-gray-600">
										<CommentToggleButton
											isExpanded={isExpanded}
											onToggle={toggleComment}
											ariaLabel={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
										/>
									</td>
								</ExpandableCommentTableRows>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
